import * as React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X, Search } from "lucide-react";

/**
 * SearchableSelect — dropdown com input de busca embutido.
 *
 * Ideal para listas grandes (500+ opções) onde um <Select> normal fica lento
 * e o usuário precisa digitar parte do nome para achar.
 *
 * Props:
 *   value          → id/valor selecionado
 *   onValueChange  → callback(value)
 *   options        → [{value, label}]
 *   placeholder    → texto quando nada selecionado
 *   clearable      → mostra X para limpar
 *   className      → classes adicionais no trigger
 *   maxHeight      → altura máx do dropdown (default 320px)
 *   minChars       → mínimo de chars antes de filtrar (default 0 — filtra sempre)
 *
 * Busca é case-insensitive, ignora acentos, e casa qualquer substring.
 * A tecla ESC fecha, ↑↓ navega, Enter seleciona.
 */
const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const SearchableSelect = React.forwardRef(function SearchableSelect(
  {
    value,
    onValueChange,
    options = [],
    placeholder = "Selecione...",
    clearable = false,
    className = "",
    maxHeight = 320,
    minChars = 0,
    "data-testid": dataTestId,
  },
  ref
) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  // Posição/direção do dropdown — recalculadas ao abrir e em scroll/resize.
  // Usamos position:fixed + portal p/ escapar de containers com overflow
  // (o form fica dentro de collapsibles com overflow:hidden que cortavam a lista).
  const [dropdownPos, setDropdownPos] = React.useState({
    top: 0, left: 0, width: 0, direction: "down", maxH: 320,
  });
  const wrapperRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const triggerRef = React.useRef(null);

  const recalcPos = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const espacoAbaixo = vh - r.bottom - 8;
    const espacoAcima = r.top - 8;
    // Dropdown precisa de ~60px do search input + no mín. 3 opções (32px cada).
    const alturaMinimaConfortavel = 200;
    // Prefere abrir para baixo, mas se não couber e tiver mais espaço acima, flipa.
    const flipUp = espacoAbaixo < alturaMinimaConfortavel && espacoAcima > espacoAbaixo;
    const maxH = Math.max(160, Math.min(maxHeight + 60, flipUp ? espacoAcima : espacoAbaixo));
    setDropdownPos({
      top: flipUp ? r.top - 4 : r.bottom + 4,
      left: r.left,
      width: r.width,
      direction: flipUp ? "up" : "down",
      maxH,
    });
  }, [maxHeight]);

  const selected = React.useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const filtered = React.useMemo(() => {
    const q = normalize(query.trim());
    if (!q || q.length < minChars) return options;
    return options.filter((o) => normalize(o.label).includes(q));
  }, [options, query, minChars]);

  const dropdownRef = React.useRef(null);

  // Fecha ao clicar fora — tanto do wrapper (trigger) quanto do dropdown (portal).
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      const dentroTrigger = wrapperRef.current?.contains(e.target);
      const dentroDropdown = dropdownRef.current?.contains(e.target);
      if (!dentroTrigger && !dentroDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Foca input ao abrir + reset + calcula posição
  React.useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      recalcPos();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, recalcPos]);

  // Recalcula posição em scroll/resize enquanto aberto (o layout muda por baixo).
  React.useEffect(() => {
    if (!open) return;
    const handler = () => recalcPos();
    window.addEventListener("scroll", handler, true);   // true = captura scrolls internos
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, recalcPos]);

  // Mantém item destacado visível dentro do scroll
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open]);

  const commit = (opt) => {
    onValueChange?.(opt?.value ?? "");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) commit(filtered[highlight]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={dataTestId}>
      <button
        type="button"
        ref={(el) => {
          triggerRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        onClick={() => setOpen((v) => !v)}
        className={`relative w-full h-10 pl-3 pr-10 rounded-lg bg-white/10 border border-brand-ice/20 text-brand-ice text-left text-sm hover:border-brand-light/40 focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light focus:outline-none transition-colors ${className}`}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
      >
        <span className={selected ? "text-brand-ice" : "text-brand-ice/50"}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {clearable && selected && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onValueChange?.("");
              }}
              className="p-0.5 rounded hover:bg-white/10 text-brand-ice/60 hover:text-brand-ice"
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-brand-ice/60 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPos.direction === "down" ? dropdownPos.top : undefined,
            bottom: dropdownPos.direction === "up" ? window.innerHeight - dropdownPos.top : undefined,
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: `${dropdownPos.maxH}px`,
            zIndex: 9999,
          }}
          className="bg-brand-dark border border-brand-ice/20 rounded-lg shadow-2xl overflow-hidden flex flex-col"
          data-testid={dataTestId ? `${dataTestId}-dropdown` : undefined}
        >
          <div className="p-2 border-b border-brand-ice/10 flex-shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-brand-ice/40 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlight(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Buscar..."
                className={`w-full h-8 pl-7 ${query ? "pr-8" : "pr-2"} rounded bg-white/5 border border-brand-ice/10 text-brand-ice text-xs placeholder:text-brand-ice/30 focus:outline-none focus:border-brand-light/40`}
                data-testid={dataTestId ? `${dataTestId}-search` : undefined}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setHighlight(0);
                    inputRef.current?.focus();
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-white/10 text-brand-ice/50 hover:text-brand-ice transition-colors"
                  title="Limpar busca"
                  data-testid={dataTestId ? `${dataTestId}-search-clear` : undefined}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <ul
            ref={listRef}
            className="overflow-y-auto text-sm flex-1 min-h-0"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-brand-ice/40 text-xs text-center italic">
                Nenhum resultado
              </li>
            ) : (
              filtered.map((opt, idx) => (
                <li
                  key={opt.value}
                  onClick={() => commit(opt)}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`px-3 py-2 cursor-pointer text-brand-ice transition-colors ${
                    idx === highlight ? "bg-brand-light/20" : ""
                  } ${
                    String(opt.value) === String(value)
                      ? "font-bold text-brand-light"
                      : ""
                  }`}
                  data-testid={
                    dataTestId ? `${dataTestId}-option-${opt.value}` : undefined
                  }
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
          {filtered.length > 0 && (
            <div className="px-3 py-1 bg-white/[0.03] border-t border-brand-ice/10 text-[10px] text-brand-ice/40 flex-shrink-0">
              {filtered.length} {filtered.length === 1 ? "resultado" : "resultados"}
              {options.length !== filtered.length && ` de ${options.length}`}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
});
